import { z } from 'zod';

export const ACTIVITY_VOCABULARY_VERSION = 1;

export const activityPresence = z.enum(['patient_visible', 'not_visible', 'other_person_only', 'uncertain']);
export const activityPosture = z.enum(['lying', 'seated', 'standing', 'walking', 'transitioning', 'on_floor', 'unknown']);
export const activityRoom = z.enum([
  'bedroom',
  'living_room',
  'kitchen',
  'hallway',
  'bathroom',
  'entrance',
  'outside',
  'unknown',
]);
export const activityFurniture = z.enum(['bed', 'chair', 'none', 'unknown']);
export const activityWake = z.enum(['awake', 'likely_asleep', 'uncertain']);
export const activityActivity = z.enum(['resting', 'eating', 'exercising', 'other', 'unknown']);

export type ActivityPresence = z.infer<typeof activityPresence>;
export type ActivityPosture = z.infer<typeof activityPosture>;
export type ActivityRoom = z.infer<typeof activityRoom>;
export type ActivityFurniture = z.infer<typeof activityFurniture>;
export type ActivityWake = z.infer<typeof activityWake>;
export type ActivityActivity = z.infer<typeof activityActivity>;

export const ACTIVITY_DIMENSION_NAMES = ['presence', 'posture', 'room', 'furniture', 'wake', 'activity'] as const;
export type ActivityDimensionName = (typeof ACTIVITY_DIMENSION_NAMES)[number];

export const ACTIVITY_DIMENSION_VALUES = {
  presence: activityPresence.options,
  posture: activityPosture.options,
  room: activityRoom.options,
  furniture: activityFurniture.options,
  wake: activityWake.options,
  activity: activityActivity.options,
} as const;

export const ACTIVITY_DIMENSION_FALLBACK = {
  presence: 'uncertain',
  posture: 'unknown',
  room: 'unknown',
  furniture: 'unknown',
  wake: 'uncertain',
  activity: 'unknown',
} as const satisfies { [K in ActivityDimensionName]: (typeof ACTIVITY_DIMENSION_VALUES)[K][number] };

function dimensionValue<T extends z.ZodType<string>>(value: T) {
  return z.object({ value, confidence: z.number().min(0).max(1) });
}

export const activityDimensions = z.object({
  vocabularyVersion: z.literal(ACTIVITY_VOCABULARY_VERSION),
  presence: dimensionValue(activityPresence),
  posture: dimensionValue(activityPosture),
  room: dimensionValue(activityRoom),
  furniture: dimensionValue(activityFurniture),
  wake: dimensionValue(activityWake),
  activity: dimensionValue(activityActivity),
});
export type ActivityDimensions = z.infer<typeof activityDimensions>;

/** Values without confidences, every dimension optional: what a reviewer asserts. */
export const activityDimensionValues = z.object({
  presence: activityPresence.optional(),
  posture: activityPosture.optional(),
  room: activityRoom.optional(),
  furniture: activityFurniture.optional(),
  wake: activityWake.optional(),
  activity: activityActivity.optional(),
});
export type ActivityDimensionValues = z.infer<typeof activityDimensionValues>;

export function hasAnyDimensionValue(values: ActivityDimensionValues | undefined): boolean {
  return values !== undefined && ACTIVITY_DIMENSION_NAMES.some((name) => values[name] !== undefined);
}

export type CompleteDimensionValues = Required<ActivityDimensionValues>;

/** Overrides win, then the block's own values, then the vocabulary fallback. */
export function mergeDimensionValues(
  base: ActivityDimensions | null,
  overrides: ActivityDimensionValues = {},
): CompleteDimensionValues {
  return {
    presence: overrides.presence ?? base?.presence.value ?? ACTIVITY_DIMENSION_FALLBACK.presence,
    posture: overrides.posture ?? base?.posture.value ?? ACTIVITY_DIMENSION_FALLBACK.posture,
    room: overrides.room ?? base?.room.value ?? ACTIVITY_DIMENSION_FALLBACK.room,
    furniture: overrides.furniture ?? base?.furniture.value ?? ACTIVITY_DIMENSION_FALLBACK.furniture,
    wake: overrides.wake ?? base?.wake.value ?? ACTIVITY_DIMENSION_FALLBACK.wake,
    activity: overrides.activity ?? base?.activity.value ?? ACTIVITY_DIMENSION_FALLBACK.activity,
  };
}

/** A block a human asserted: every value carries full confidence. */
export function dimensionsFromValues(values: CompleteDimensionValues): ActivityDimensions {
  return {
    vocabularyVersion: ACTIVITY_VOCABULARY_VERSION,
    presence: { value: values.presence, confidence: 1 },
    posture: { value: values.posture, confidence: 1 },
    room: { value: values.room, confidence: 1 },
    furniture: { value: values.furniture, confidence: 1 },
    wake: { value: values.wake, confidence: 1 },
    activity: { value: values.activity, confidence: 1 },
  };
}

export interface ActivityDimensionValue<T extends string> {
  value: T;
  confidence: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function clampConfidence(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return 0;
  }
  return Math.min(1, Math.max(0, raw));
}

function readDimension<T extends string>(
  raw: unknown,
  options: readonly T[],
  fallback: T,
): ActivityDimensionValue<T> {
  const source = isRecord(raw) ? raw : {};
  const found = options.find((option) => option === source['value']);
  if (found === undefined) {
    return { value: fallback, confidence: 0 };
  }
  return { value: found, confidence: clampConfidence(source['confidence']) };
}

export const ACTIVITY_OBSERVED_DIMENSION_NAMES = ['posture', 'room', 'furniture', 'wake', 'activity'] as const;
export type ActivityObservedDimensionName = (typeof ACTIVITY_OBSERVED_DIMENSION_NAMES)[number];

export const activityObservedDimensions = z.object({
  vocabularyVersion: z.literal(ACTIVITY_VOCABULARY_VERSION),
  posture: dimensionValue(activityPosture),
  room: dimensionValue(activityRoom),
  furniture: dimensionValue(activityFurniture),
  wake: dimensionValue(activityWake),
  activity: dimensionValue(activityActivity),
});
export type ActivityObservedDimensions = z.infer<typeof activityObservedDimensions>;

function hasCompatibleVocabulary(raw: Record<string, unknown>): boolean {
  const version = raw['vocabularyVersion'];
  return version === undefined || version === ACTIVITY_VOCABULARY_VERSION;
}

function readObservedDimensions(raw: Record<string, unknown>): Omit<ActivityObservedDimensions, 'vocabularyVersion'> {
  const values = ACTIVITY_DIMENSION_VALUES;
  const fallback = ACTIVITY_DIMENSION_FALLBACK;
  return {
    posture: readDimension(raw['posture'], values.posture, fallback.posture),
    room: readDimension(raw['room'], values.room, fallback.room),
    furniture: readDimension(raw['furniture'], values.furniture, fallback.furniture),
    wake: readDimension(raw['wake'], values.wake, fallback.wake),
    activity: readDimension(raw['activity'], values.activity, fallback.activity),
  };
}

export function parseActivityDimensions(raw: unknown): ActivityDimensions | null {
  if (!isRecord(raw) || !hasCompatibleVocabulary(raw)) {
    return null;
  }
  return {
    vocabularyVersion: ACTIVITY_VOCABULARY_VERSION,
    presence: readDimension(raw['presence'], ACTIVITY_DIMENSION_VALUES.presence, ACTIVITY_DIMENSION_FALLBACK.presence),
    ...readObservedDimensions(raw),
  };
}

export function parseObservedDimensions(raw: unknown): ActivityObservedDimensions | null {
  if (!isRecord(raw) || !hasCompatibleVocabulary(raw)) {
    return null;
  }
  return { vocabularyVersion: ACTIVITY_VOCABULARY_VERSION, ...readObservedDimensions(raw) };
}

function constraintOf<T extends z.ZodType<string>>(value: T) {
  const many = z.array(value).min(1);
  return z.union([value, many, z.strictObject({ not: z.union([value, many]) })]);
}

export const codeDimensionRequires = z.strictObject({
  presence: constraintOf(activityPresence).optional(),
  posture: constraintOf(activityPosture).optional(),
  room: constraintOf(activityRoom).optional(),
  furniture: constraintOf(activityFurniture).optional(),
  wake: constraintOf(activityWake).optional(),
  activity: constraintOf(activityActivity).optional(),
});
export type CodeDimensionRequires = z.infer<typeof codeDimensionRequires>;

export const codeDimensionTemporal = z.strictObject({
  previousCodes: z.array(z.string().min(1)).min(1),
});

export const codeDimensionRule = z
  .strictObject({
    requires: codeDimensionRequires,
    priority: z.number().int().min(0),
    temporal: codeDimensionTemporal.optional(),
  })
  .refine((rule) => ACTIVITY_DIMENSION_NAMES.some((name) => rule.requires[name] !== undefined), {
    path: ['requires'],
    message: 'At least one dimension constraint is required',
  });
export type CodeDimensionRule = z.infer<typeof codeDimensionRule>;

export type CodeDimensionConstraint<T extends string = string> = T | T[] | { not: T | T[] };

function toArray<T extends string>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

function sortedUnique<T extends string>(values: T[]): T[] {
  return [...new Set(values)].sort();
}

function normalizeConstraint<T extends string>(constraint: CodeDimensionConstraint<T>): T[] | { not: T[] } {
  if (typeof constraint === 'string' || Array.isArray(constraint)) {
    return sortedUnique(toArray(constraint));
  }
  return { not: sortedUnique(toArray(constraint.not)) };
}

function normalizeRequires(requires: CodeDimensionRequires): CodeDimensionRequires {
  const normalized: CodeDimensionRequires = {};
  if (requires.presence !== undefined) {
    normalized.presence = normalizeConstraint(requires.presence);
  }
  if (requires.posture !== undefined) {
    normalized.posture = normalizeConstraint(requires.posture);
  }
  if (requires.room !== undefined) {
    normalized.room = normalizeConstraint(requires.room);
  }
  if (requires.furniture !== undefined) {
    normalized.furniture = normalizeConstraint(requires.furniture);
  }
  if (requires.wake !== undefined) {
    normalized.wake = normalizeConstraint(requires.wake);
  }
  if (requires.activity !== undefined) {
    normalized.activity = normalizeConstraint(requires.activity);
  }
  return normalized;
}

export function normalizeCodeDimensionRule(rule: CodeDimensionRule): CodeDimensionRule {
  const normalized: CodeDimensionRule = { requires: normalizeRequires(rule.requires), priority: rule.priority };
  if (rule.temporal) {
    normalized.temporal = { previousCodes: sortedUnique(rule.temporal.previousCodes) };
  }
  return normalized;
}

export function codeDimensionRuleKey(rule: CodeDimensionRule): string {
  return JSON.stringify(normalizeCodeDimensionRule(rule));
}

export function codeDimensionRuleEquals(left: CodeDimensionRule, right: CodeDimensionRule): boolean {
  return codeDimensionRuleKey(left) === codeDimensionRuleKey(right);
}

export const PATIENT_NOT_VISIBLE_CODE = { code: '85', description: 'not visible' } as const;

const ON_FEET: ActivityPosture[] = ['standing', 'walking', 'transitioning'];
const STANDING_OR_WALKING: ActivityPosture[] = ['standing', 'walking'];
const NOT_VISIBLE: ActivityPresence[] = ['not_visible', 'other_person_only'];
const AWAKE_OR_UNCERTAIN: ActivityWake[] = ['awake', 'uncertain'];

export const CODE_DIMENSION_RULES: Readonly<Record<string, CodeDimensionRule>> = {
  '71': { requires: { posture: 'on_floor' }, priority: 100 },
  '40': { requires: { activity: 'eating', posture: STANDING_OR_WALKING }, priority: 79 },
  '41': { requires: { activity: 'eating', room: 'bedroom' }, priority: 80 },
  '42': { requires: { activity: 'eating', room: 'living_room' }, priority: 80 },
  '43': { requires: { activity: 'eating', room: 'kitchen' }, priority: 80 },
  '61': { requires: { posture: STANDING_OR_WALKING, room: 'entrance' }, priority: 70 },
  '31': { requires: { posture: ON_FEET, room: 'bedroom' }, priority: 60 },
  '32': { requires: { posture: ON_FEET, room: 'living_room' }, priority: 60 },
  '33': { requires: { posture: ON_FEET, room: 'kitchen', activity: { not: 'eating' } }, priority: 60 },
  '34': { requires: { posture: ON_FEET, room: 'hallway' }, priority: 60 },
  '30': { requires: { posture: 'walking', room: ['unknown', 'entrance'] }, priority: 50 },
  '51': { requires: { room: 'bathroom' }, priority: 50 },
  '23': {
    requires: {
      room: 'kitchen',
      posture: ['seated', 'lying', 'transitioning', 'unknown'],
      activity: { not: 'eating' },
    },
    priority: 45,
  },
  '11': { requires: { furniture: 'bed', wake: 'likely_asleep' }, priority: 40 },
  '12': { requires: { furniture: 'chair', wake: 'likely_asleep' }, priority: 40 },
  '21': { requires: { furniture: 'bed', wake: AWAKE_OR_UNCERTAIN, activity: { not: 'eating' } }, priority: 40 },
  '22': {
    requires: { furniture: 'chair', wake: AWAKE_OR_UNCERTAIN, activity: { not: 'eating' }, room: { not: 'kitchen' } },
    priority: 40,
  },
  [PATIENT_NOT_VISIBLE_CODE.code]: { requires: { presence: NOT_VISIBLE }, priority: 10 },
  '73': { requires: { posture: 'on_floor' }, priority: 110, temporal: { previousCodes: ['71', '73'] } },
  '72': {
    requires: { presence: 'patient_visible', posture: { not: 'on_floor' } },
    priority: 90,
    temporal: { previousCodes: ['71', '73'] },
  },
  '52': {
    requires: { presence: 'patient_visible', room: { not: 'bathroom' } },
    priority: 65,
    temporal: { previousCodes: ['51', '53'] },
  },
  '63': {
    requires: { presence: 'patient_visible', room: { not: ['entrance', 'outside'] } },
    priority: 65,
    temporal: { previousCodes: ['61'] },
  },
  '62': { requires: { presence: NOT_VISIBLE }, priority: 35, temporal: { previousCodes: ['61', '62'] } },
  '53': { requires: { presence: NOT_VISIBLE }, priority: 30, temporal: { previousCodes: ['51', '53'] } },
  '39': {
    requires: { presence: NOT_VISIBLE },
    priority: 20,
    temporal: { previousCodes: ['30', '31', '32', '33', '34'] },
  },
};
